
import Vue, { PropOptions } from 'vue';
import _ from 'lodash';
import { contactName } from '@/helpers/contactName';
import { dateFormat } from '@/helpers/dateFormat';
import { numFormat, unitPriceFormat } from '@/helpers/numFormat';
import { convertToArabicNumber } from '@/helpers/convertToArabicNumber';
import { productAlias } from '@/helpers/productAlias';
import { baseUnit } from '@/helpers/baseUnit';
import { QuotationPrintView } from '@/models/page-models/page-items/QuotationPrintView';

export default Vue.extend({
  props: {
    input: { type: Object, default: () => {} } as PropOptions<QuotationPrintView>,
    data: { type: Object, default: () => ({}) },
    getVariable: { type: Function, default: () => () => {} },
    callFunction: { type: Function, default: () => () => {} },
    onChange: { type: Function, default: () => {} },
    parent: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      printLanguage: 'en'
    };
  },
  computed: {
    isEn(): boolean {
      return this.printLanguage === 'en';
    },
    quotationDetails(): any {
      return this.getVariable(this.input.variable);
    },
    customerDetails(): any {
      return this.getVariable(this.input.config.customerDetailsVariable);
    },
    companyDetails(): any {
      return this.getVariable(this.input.config.companyDetailsVariable);
    },
    vatPercent(): number {
      return this.getVariable(this.input.config.vatPercentVariable);
    },
    contactName(): string {
      return this.quotationDetails?.contact ? contactName(this.quotationDetails.contact) : '';
    },
    termsAndConditions(): any {
      return _.get(this.quotationDetails, 'termsAndConditions', {});
    },
    tradeTerms(): { type: string, en: string, ar: string } {
      const tradeTermsList = this.getVariable(this.input.config.tradeTermsListVariable) || [];
      return _.find(tradeTermsList, (item) => item.type === this.termsAndConditions.tradeTerms?.type)
        || { type: this.termsAndConditions.tradeTerms?.type || '', en: '', ar: '' };
    },
    paymentTerms(): { alias: string, type: string, en: string, ar: string } | undefined {
      const paymentTermsList = this.getVariable(this.input.config.paymentTermsListVariable) || [];
      return _.find(paymentTermsList, (item) => item.alias === this.termsAndConditions.paymentTerms?.type);
    },
    tradeTermAddress(): string {
      const deliveryLocation = _.get(this.quotationDetails, 'deliveryLocations.0', '');
      const addresses = _.get(this.quotationDetails, 'customer.deliveryAddresses', []);
      const addressComponents = _.get(_.find(addresses, (address: any) => address.ref === deliveryLocation), 'location.address_components', []);
      const locality = _.get(_.find(addressComponents, (comp: any) => _.includes(comp.types, 'locality')), 'long_name');
      const country = _.get(_.find(addressComponents, (comp: any) => _.includes(comp.types, 'country')), 'long_name');
      return locality && country ? `${locality}, ${country}` : '';
    },
    hasFOC(): boolean {
      return (this.quotationDetails?.items || []).some((item: any) => item.totalFOC > 0);
    },
    totals(): { subTotal: number, vatValue: number, totalAmount: number } {
      const items = this.quotationDetails?.items || [];
      let subTotal = 0;
      let vatValue = 0;
      let totalAmount = 0;

      items.forEach((item: any) => {
        const itemTotal = this.getTotal(item);
        const itemVat = itemTotal * (this.vatPercent / 100);
       
        subTotal += itemTotal;
        vatValue += itemVat;
        totalAmount += (itemTotal + itemVat);
      });

      return { subTotal, vatValue, totalAmount };
    }
  },
  methods: {
    triggerPrint(lang: string) {
      this.printLanguage = lang;
      this.$nextTick(() => {
        window.print();
      });
    },
    convertToArabicNo(val: number | string, useCommas = false, allowExtraDecimal = false): string {
      if (!val && val !== 0) return '';
      return convertToArabicNumber(Number(val), useCommas, allowExtraDecimal);
    },
    formatNumber(val: any): string {
      const num = Number(val);
      if (isNaN(num)) return '';
      const formattedVal = String(numFormat(num));
      return this.isEn ? formattedVal : this.convertToArabicNo(num, true);
    },
    formatUnitPrice(val: any): string {
      const num = Number(val);
      if (isNaN(num)) return '';
      const formattedVal = String(unitPriceFormat(num, true));
      return this.isEn ? formattedVal : this.convertToArabicNo(num, false, true);
    },
    formatDate(date: string, format?: string) {
      return dateFormat(date, format);
    },
    getTotal(item: any): number {
      const price = _.get(item, 'productPrice.price', 0);
      const qty = _.get(item, 'totalQuantity', 0);
      return price * qty;
    },
    getProductAlias(product: any): string {
      return productAlias(product);
    },
    getBaseUnit(product: any): string {
      return baseUnit(product);
    }
  }
});
